import type { RecordedSet, SessionData } from '../types';

interface CompletionPhaseProps {
  session: SessionData;
  recordedSets: RecordedSet[];
  startedAt: number | null;
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function CompletionPhase({ session, recordedSets, startedAt }: CompletionPhaseProps) {
  const totalTime = startedAt ? formatDuration(Date.now() - startedAt) : '—';
  const uniqueExercises = new Set(recordedSets.map((s) => s.exerciseId)).size;
  const totalReps = recordedSets.reduce((sum, s) => sum + (s.repsCompleted ?? 0), 0);
  const maxRound = recordedSets.reduce((max, s) => Math.max(max, s.roundNumber ?? 0), 0);

  return (
    <div className="flex flex-col items-center gap-6 p-6" data-testid="workout-session-summary">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900">
        <svg className="h-8 w-8 text-brand-600 dark:text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <h2 className="text-heading-xl text-gray-900 dark:text-textDark-primary">
        Session Complete — Well Done
      </h2>

      {/* Stats row */}
      <div className="grid grid-cols-5 w-full max-w-lg gap-2 rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
        <div className="text-center">
          <p className="text-heading-lg text-brand-600 dark:text-brand-400" data-testid="workout-total-time">
            {totalTime}
          </p>
          <p className="text-meta text-gray-500 dark:text-textDark-muted">Duration</p>
        </div>
        <div className="text-center">
          <p className="text-heading-lg text-brand-600 dark:text-brand-400">
            {maxRound}
          </p>
          <p className="text-meta text-gray-500 dark:text-textDark-muted">Rounds</p>
        </div>
        <div className="text-center">
          <p className="text-heading-lg text-brand-600 dark:text-brand-400" data-testid="workout-exercises-completed">
            {uniqueExercises}
          </p>
          <p className="text-meta text-gray-500 dark:text-textDark-muted">Exercises</p>
        </div>
        <div className="text-center">
          <p className="text-heading-lg text-brand-600 dark:text-brand-400">
            {totalReps}
          </p>
          <p className="text-meta text-gray-500 dark:text-textDark-muted">Reps</p>
        </div>
        <div className="text-center">
          <p className="text-heading-lg text-brand-600 dark:text-brand-400">
            {recordedSets.length}
          </p>
          <p className="text-meta text-gray-500 dark:text-textDark-muted">Sets</p>
        </div>
      </div>

      {/* Per-exercise breakdown */}
      <div className="w-full max-w-lg">
        <h3 className="mb-2 text-heading-md text-gray-900 dark:text-textDark-primary">Breakdown</h3>
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-dark-border">
          <table className="w-full text-left text-body">
            <thead className="bg-gray-50 text-meta text-gray-500 dark:bg-dark-elevated dark:text-textDark-muted">
              <tr>
                <th className="px-4 py-2">Exercise</th>
                <th className="px-4 py-2 text-right">Reps</th>
                <th className="px-4 py-2 text-right">Weight</th>
                <th className="px-4 py-2 text-right">Round</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-dark-border dark:bg-dark-surface">
              {recordedSets.map((set, i) => (
                <tr key={i}>
                  <td className="px-4 py-2 text-gray-900 dark:text-textDark-primary">
                    {set.exerciseName}
                    {set.side && <span className="ml-1 text-meta text-gray-400">({set.side})</span>}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-textDark-secondary">
                    {set.repsCompleted ?? (set.durationCompletedSeconds != null ? `${set.durationCompletedSeconds}s` : '—')}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-textDark-secondary">
                    {set.weightUsedKg != null ? `${set.weightUsedKg}kg` : '—'}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-700 dark:text-textDark-secondary">
                    {set.roundNumber}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex w-full max-w-lg gap-3">
        <a
          href={`/sessions/detail/${session.id}`}
          className="flex-1 rounded-md border border-gray-300 px-4 py-3 text-center text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-dark-border dark:text-textDark-secondary dark:hover:bg-dark-elevated"
          data-testid="workout-view-history-button"
        >
          View Session
        </a>
        <a
          href="/sessions"
          className="flex-1 rounded-md bg-brand-600 px-4 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-brand-700 active:bg-brand-800"
          data-testid="workout-finish-session-button"
        >
          View All Sessions
        </a>
      </div>
    </div>
  );
}
